import omit from "lodash/omit";
import qs from "qs";
import React, { useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation } from "react-router-dom";

import { useDealerData } from "~/app/Context/DealerContext";
import { Filter } from "~/features/filter";
import { FILTER_BY } from "~/pages/Staff/assets/StaffFilter/lib";
import { usePermissions } from "~/shared/context/PermissionContext";

import { STAFF_BONUS_FILTER_KEYS, staffBonusFilterFields } from "./fields";
import { useExpectationOptionsData } from "./optionsHooks";

// Дефолты старой формы (defaultStaffFilterValues): измерение «по дилеру»,
// текущий дилер, текущая дата. dealerId берём реактивно из DealerContext:
// sessionStorage заполняется асинхронно (после ответа dealersMy / соглашения),
// и одноразовое чтение на маунте теряло dealerId.
const getDefaultParams = (dealerId?: string | null): Record<string, string> => {
  const defaults: Record<string, string> = {
    filterBy: FILTER_BY.DEALER,
    period: new Date().toISOString().split("T")[0],
  };
  const currentDealerId = dealerId || sessionStorage.getItem("currentDealerId");
  if (currentDealerId) {
    defaults.filterItems = currentDealerId;
  }
  return defaults;
};

const StaffBonusFilter = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const { DealerId } = useDealerData();
  const { hasPermission } = usePermissions();
  // PASDILER-2325: поле «Должность» лочится правом, а не dealerChoice.
  const canEditExpectations = hasPermission(
    "employeeAccounting.bonus.columns.update",
  );

  const fields = useMemo(
    () => staffBonusFilterFields(t, canEditExpectations),
    [t, canEditExpectations],
  );

  const { combinableIds } = useExpectationOptionsData();

  // Подставляем дефолты в URL при первом входе, если не заданы явно.
  // Один эффект на все инъекции: два history.replace подряд со «старым»
  // location.search затирали друг друга и плодили промежуточные запросы
  // с неполным фильтром.
  // Эффект перезапускается при появлении DealerId: текущий дилер может
  // подгрузиться позже маунта (dealersMy, соглашение), и без этого
  // filterItems оставался пустым — dealerId терялся в запросе.
  // Ожидания, объединяющие обучение/сертификацию, подставляются один раз
  // после загрузки справочника, если параметр не задан явно
  // (после сброса не восстанавливаем — этим занимается handleReset).
  const expectationsInjectedRef = useRef(false);
  useEffect(() => {
    const params = location.search ? qs.parse(location.search.slice(1)) : {};
    const defaults = getDefaultParams(DealerId);
    const missing = Object.keys(defaults).filter((key) => !params[key]);
    const needExpectations =
      !expectationsInjectedRef.current &&
      combinableIds.length > 0 &&
      !params.filterExpectations;
    if (!missing.length && !needExpectations) return;
    if (needExpectations) expectationsInjectedRef.current = true;
    history.replace({
      search: qs.stringify({
        ...params,
        ...missing.reduce((acc, key) => ({ ...acc, [key]: defaults[key] }), {}),
        ...(needExpectations
          ? { filterExpectations: combinableIds.join(",") }
          : {}),
      }),
    });
  }, [DealerId, combinableIds]);

  // Сброс возвращает дефолты — так работал resetForm старой формы.
  // Без права на редактирование поле должностей задизейблено и всегда
  // заполнено — восстанавливаем ожидания при сбросе.
  const handleReset = () => {
    const params = location.search ? qs.parse(location.search.slice(1)) : {};
    const rest = omit(params, [...STAFF_BONUS_FILTER_KEYS, "page"]);
    history.replace({
      search: qs.stringify({
        ...rest,
        ...getDefaultParams(DealerId),
        ...(!canEditExpectations && combinableIds.length
          ? { filterExpectations: combinableIds.join(",") }
          : {}),
      }),
    });
  };

  return (
    <Filter.Root fields={fields} alwaysShow columns={4} onReset={handleReset} />
  );
};

export default StaffBonusFilter;
